import { HolidaysTypes } from "date-holidays"
import { CalendarWeek } from "src/app/_models/planning-assistant.interface"
import { PAOperation } from "./operation"
import { PAUtil, WeekDay } from "./util"
import { PACoordinates } from "./coordinates"
import { PATechnician } from "./technician"
import { TravelTechnicianDateService } from "src/app/_services/travel_technician_date.service"
import { TravelTechnicianDate } from "./travel-technician-date"
import { Subscription } from "rxjs"
import { PATour } from "./tour";
import { PALocation } from "./location";
import { PAStore } from "./store";
import { InsertionConfig } from "../tourplanner-menu/tour-timeline/tour-timeline.component";
import { FireUpdateOnChange } from "./util-classes/fire-update-on-change";
import { PADataControl } from "../singletons/pa-data-control";
import { PATourPlannerControl } from "../singletons/pa-tourplanner-control";
import { PATimeControl } from "../singletons/pa-time-control";
import { PAFilterControl } from "../singletons/pa-filter-control";
import { PASettingsControl } from "../singletons/pa-settings-control";
import { DraggableDestinationMarker } from "./draggable-marker";
import { PAPlanningInstructions } from "./planning-instructions";
import { MatSnackBarConfig } from "@angular/material/snack-bar";
import {
  NO_LOCATION_CHANGE_FOR_DESTINATION_COORDINATES,
  NO_LOCATION_CHANGE_FOR_PROJECTS
} from "../../../_models/technician-tour.model";
import { Absence } from "../../../_models/absence.interface";

interface NewTourConfig {
  force_new_tour?: boolean;
  insert_operations?: PAOperation[];
  delete_operations?: PAOperation[];
  custom_start_location?: {location: PALocation, marker: DraggableDestinationMarker} | 'remove'
  custom_end_location?: {location: PALocation, marker: DraggableDestinationMarker} | 'remove'
}

interface Insertion {
  operation: PAOperation;
  start_time_ms: number;
  tour_time_ms?: number;
  end_time_ms: number
}

function getRoutesLastFixedLocation(route: PAOperation[]) {
  let found_location: PALocation
  for (let i = route.length - 1; i >= 0; i--) {
    const ticket = route[i].ticket
    const location = ticket.location
    const coords = location.coordinates
    const invalid_coordinates = NO_LOCATION_CHANGE_FOR_DESTINATION_COORDINATES.find(c => c.lng == coords.longitude && c.lat == coords.latitude)
    const invalid_project = NO_LOCATION_CHANGE_FOR_PROJECTS.includes(ticket.project.id)
    if (!invalid_coordinates && !invalid_project) {
      found_location = location
    }
  }
  return found_location;
}

function recursiveInsertionShift(insertions: Insertion[], shift_by_ms: number): Insertion[] {
  const next_appointment_insertion = insertions.find(ins => ins.operation.date_fixed)

  if (!next_appointment_insertion) {
    // Case: No more Appointments -> normal shift till the end
    insertions.map(
      insertion => {
        insertion.start_time_ms += shift_by_ms
        insertion.end_time_ms += shift_by_ms
      }
    )
    return [...insertions]
  } else {
    // Case more Appointments
    const current_insertion_index = insertions.indexOf(next_appointment_insertion)
    const insertions_before_appointment = insertions.filter(ins => insertions.indexOf(ins) < current_insertion_index)
    const insertions_after_appointment = insertions.filter(ins => insertions.indexOf(ins) > current_insertion_index)

    const fitting_insertions: Insertion[] = []
    const unfitting_insertions: Insertion[] = []
    let new_shift_ms = 0
    for (let insertion of insertions_before_appointment) {
      const shifted_end_time = insertion.end_time_ms + shift_by_ms
      if (shifted_end_time <= next_appointment_insertion.start_time_ms) {
        fitting_insertions.push(insertion)
        insertion.start_time_ms += shift_by_ms
        insertion.end_time_ms += shift_by_ms
      } else {
        unfitting_insertions.push(insertion)
        new_shift_ms += insertion.end_time_ms - insertion.start_time_ms
      }
    }

    if (unfitting_insertions.length) {
      const ms_between_last_unfitting_insertion_an_appointment_insertion = next_appointment_insertion.start_time_ms - unfitting_insertions[unfitting_insertions.length - 1].end_time_ms
      const appointment_insertion_duration = next_appointment_insertion.end_time_ms - next_appointment_insertion.start_time_ms
      for (let insertion of unfitting_insertions) {
        let pre_shift_ms = appointment_insertion_duration + ms_between_last_unfitting_insertion_an_appointment_insertion + shift_by_ms
        insertion.start_time_ms += pre_shift_ms
        insertion.end_time_ms += pre_shift_ms
      }
    }

    const new_shift_insertions = unfitting_insertions.concat(insertions_after_appointment)
    return fitting_insertions.concat([next_appointment_insertion], recursiveInsertionShift(new_shift_insertions, new_shift_ms))
  }
}

function shiftInsertionsByMinutes(insertions: Insertion[], shift_start_idx: number, shift_by_minutes: number): Insertion[] {
  let shift_by_ms = shift_by_minutes * 60000
  const not_shifted_insertions = insertions.filter(ins => insertions.indexOf(ins) < shift_start_idx)
  const shifted_insertions = insertions.filter(ins => insertions.indexOf(ins) >= shift_start_idx)
  return not_shifted_insertions.concat(recursiveInsertionShift(shifted_insertions, shift_by_ms))
}

export class PATechnicianDate extends FireUpdateOnChange<'new_tour'> {

  static travelTechnicianDates = new Map<number, Map<number, TravelTechnicianDate> | 'loading'>()
  static travelTechnicianDateService: TravelTechnicianDateService
  static defaultMaxWorkTime = 480
  static skipOperationUpdatesForMilliseconds = 1000 * 60 * 10 // 5min

  public loadingStatus: 'init' | 'loading' | 'loaded' = 'init'

  private _loadingTravelTechnicianDates = false
  public previousTravelTechnicianDate: TravelTechnicianDate
  public previousTravelTechnicianDateChangeSubscription: Subscription
  public travelTechnicianDate: TravelTechnicianDate
  public travelTechnicianDateChangeSubscription: Subscription

  public latestNewOperationUpdateTimestamp = 0
  freeTourIndexes: number[] = []
  automaticLunchBreakCalculation = true
  lunchBreakAfterOperationNumber: number
  insertionConfigMap = new Map<number, InsertionConfig>()
  private _newTourConfig: NewTourConfig = {}
  public newTourTouched = false

  tour: PATour
  readonly isPastTechnicianDate: boolean
  readonly isTodaysTechnicianDate: boolean

  changedTourContainer: ChangedTourContainer

  public absences: Absence[] = []
  readonly noShift: boolean

  constructor(
    public technician: PATechnician,
    public date: string,
    public day: {
      week_day: WeekDay,
      utc_timestamp: number,
      local_timestamp: number,
      holidays: HolidaysTypes.Holiday[],
      date_string: string
    },
    public hawk_link: string,
    public id?: number
  ) {
    super()
    this.tour = this.generateNewEmptyTour()
    this.changedTourContainer = {
      tour: this.generateNewEmptyTour(),
      max_tour_minutes: 600,
      unfittingOperations: [],
      deletingOperations: []
    }
    let todays_day_timestamp = PATimeControl.Instance.dateToTimestamp(new Date(), true, true)
    this.isPastTechnicianDate = todays_day_timestamp > this.day.utc_timestamp
    this.isTodaysTechnicianDate = todays_day_timestamp == this.day.utc_timestamp

    this.noShift = !this.technician.hasShiftOnDay(this.day.utc_timestamp)
  }

  executeBeforeChange(): void {
    return
  }

  get newTourConfig(): NewTourConfig {
    return this._newTourConfig
  }

/*  selectTimelineTourForMap(skip_map_center?: boolean): void {
    PATourPlannerControl.Instance.updateSelectedTourPlannerTours()
    if (!skip_map_center) PATourPlannerControl.Instance.centerTourPlannerTourInMap(this)
  }*/

  async setTravelTechnicianDate(): Promise<void> {

    if (!this._loadingTravelTechnicianDates) {
      this._loadingTravelTechnicianDates = true
      let update_travel_status = false
      if (!this.travelTechnicianDate) {
        this.travelTechnicianDate = await this.getTravelTechnicianDate()
        this.travelTechnicianDateChangeSubscription = this.travelTechnicianDate.subscribeToChanges(() => {
          this.updateTourEndLocation()
          //this.updateNewTourOperationChanges()
          //this.changedTourContainer.tour.onTravelTechnicianDateChange()
        })
        update_travel_status = true
      }

      if (!this.previousTravelTechnicianDate) {
        let prev_technician_date = this.getPreviousTechnicianDate(true)
        if (prev_technician_date) {
          this.previousTravelTechnicianDate = await prev_technician_date.getTravelTechnicianDate()
          this.previousTravelTechnicianDateChangeSubscription = this.previousTravelTechnicianDate.subscribeToChanges(() => {
            this.updateTourStartLocation()
            //this.updateNewTourOperationChanges()
            //this.changedTourContainer.tour.onPreviousTravelTechnicianDateChange()
          })
          update_travel_status = true
        }
      }

      if (update_travel_status && this.tour) {
        this.updateTourStartLocation();
        //this.changedTourContainer.tour.updateStartLocation()
        this.updateTourEndLocation()
        //await this.changedTourContainer.tour.updateTravelStatus(true)
      }

      this._loadingTravelTechnicianDates = false
    }

    await PAUtil.sleepUntilConditionBreaks(
      () => {
        return !this.travelTechnicianDate || this._loadingTravelTechnicianDates
      }
    )
  }

  public updateTourStartLocation() {
    let current_start_location = this.getStartLocation()
    if (current_start_location != this.tour.startLocation) {
      this.tour.startLocation = current_start_location
      PATourPlannerControl.Instance.lastMainRouteLocationChangeTimestamp = Date.now()
    }
  }

  public updateTourEndLocation() {
    let current_end_location = this.getEndLocation()
    if (current_end_location != this.tour.endLocation) {
      this.tour.endLocation = current_end_location
      PATourPlannerControl.Instance.lastMainRouteLocationChangeTimestamp = Date.now()
    }
  }


  public getStartLocation(): PALocation {
    let previous_technician_date = this.getPreviousTechnicianDate()
    if (this.previousTravelTechnicianDate?.isActive() && previous_technician_date) {
      return this.previousTravelTechnicianDate.location || this.technician.location
    } else {
      return this.technician.location
    }
  }

  public getEndLocation(): PALocation {
    if (this.travelTechnicianDate?.isActive()) {
      return this.travelTechnicianDate.location || this.technician.location
    } else {
      return this.technician.location
    }
  }

  public async getTravelTechnicianDate(): Promise<TravelTechnicianDate> {
    return await PATechnicianDate.getTravelTechnicianDate(this.technician.id, this.day.utc_timestamp)
  }

  public async waitUntilDataWasLoaded(): Promise<void> {
    if (this.loadingStatus == 'init') {
      await this.loadData()
    }

    await PAUtil.sleepUntilConditionBreaks(
      () => {
        return this.loadingStatus == 'init' || this.loadingStatus == 'loading'
      }
    )
  }

  public getPublicHolidays(): HolidaysTypes.Holiday[] {
    return this.day.holidays.filter(holiday => holiday.type == 'public')
  }

  public getLocalHolidays(): HolidaysTypes.Holiday[] {
    return this.day.holidays.filter(holiday => holiday.type == 'observance')
  }

  public getCalendarWeek(): CalendarWeek {
    return PATimeControl.Instance.timestampToCalendarWeek(this.day.utc_timestamp)
  }

  public getPreviousTechnicianDate(init_if_not_existent?: boolean, load_data?: boolean): PATechnicianDate {
    return this.technician.getTechnicianDate(this.day.utc_timestamp - 24 * 60 * 60 * 1000, init_if_not_existent, load_data)
  }

  public getSubsequentTechnicianDate(init_if_not_existent?: boolean, load_data?: boolean): PATechnicianDate {
    return this.technician.getTechnicianDate(this.day.utc_timestamp + 24 * 60 * 60 * 1000, init_if_not_existent, load_data)
  }

  generateNewEmptyTour(config?: {custom_start_location?: PALocation, custom_end_location?: PALocation}): PATour {
    return new PATour(
      this,
      config?.custom_start_location || this.getStartLocation(),
      config?.custom_end_location || this.getEndLocation()
    )
  }

  public async loadData(): Promise<void> {
    if (this.loadingStatus != 'loading' && this.loadingStatus != 'loaded') {
      const utc_timestamp = this.day.utc_timestamp
      this.loadingStatus = 'loading'

      const day_operations = await PATechnician.dataLoader.getTechniciansOperationsForDayTimestamp(this.technician, utc_timestamp)
      if (this.loadingStatus == 'loading') {
        await this.executeOnLoaded()
        if (day_operations.length) {
          await PAOperation.insertOperationsInTechnicianDateAndStore(day_operations)
        }
        await this.tour.updateRoute()

      } else {
        if (day_operations.length) {
          await PADataControl.Instance.updateOperations(day_operations)
        }
      }

    }
  }

  getWeekdayIdx(): number {
    return PATimeControl.Instance.weekDays.indexOf(this.day.week_day)
  }

  hasOperationWithIdInChangedTour(op_id: number): boolean {
    let ctc = this.changedTourContainer
    return !!ctc.tour.operations.concat(ctc.unfittingOperations).find(op => op.id == op_id)
  }

  async updateNewTour(config?: NewTourConfig): Promise<void> {
    let insertion_operations = config?.insert_operations || []
    let deletion_operations = config?.delete_operations || []
    let deletion_operation_ids = deletion_operations.map(op => op.id)
    this.updateInsertionConfigMap(insertion_operations)

    const possible_tdc = PATourPlannerControl.Instance.tourPlannerOverviewTechnicianDateCollections.find(tdc => tdc.technicianDates.includes(this))
    const auto_optimize_target_location = possible_tdc ? possible_tdc.autoOptimizeTravelDestinationsForDayTimestamps.includes(this.day.utc_timestamp) : false
    const tour_will_have_operations = !!this.tour.operations.concat(insertion_operations).filter(o => !deletion_operations.find(delete_op => delete_op.id == o.id)).length
    const has_travel_start_location = (config?.custom_start_location != 'remove' && config?.custom_start_location?.location) || (this.getStartLocation() != this.technician.location)
    const set_end_location_to_last_operations_location = has_travel_start_location
      && tour_will_have_operations
      && auto_optimize_target_location

    const use_start_location = config?.custom_start_location ? (config.custom_start_location == 'remove' ? this.technician.location : config.custom_start_location.location) : this.getStartLocation()
    let use_end_location: PALocation
    if (set_end_location_to_last_operations_location) {
      use_end_location = null
    } else {
      use_end_location = config?.custom_end_location ? (config.custom_end_location == 'remove' ? this.technician.location : config.custom_end_location.location) : this.getEndLocation()
    }

    let all_operations_used_in_new_tour = insertion_operations.concat(this.tour.operations.filter(op => !deletion_operation_ids.includes(op.id)))
    let { equal_operations } = this.tour.compareRoute(all_operations_used_in_new_tour)
    let start_location_matches = this.tour.startLocation == use_start_location
    let end_location_matches = (set_end_location_to_last_operations_location && this.tour.operations.length) ? (this.tour.endLocation ==  this.tour.operations[this.tour.operations.length - 1].ticket.location) : (this.tour.endLocation == use_end_location)

    let update_ts = this.latestNewOperationUpdateTimestamp = Date.now()
    let found_valid_route: OptimizedRoute
    let lunch_break_changed = !this.automaticLunchBreakCalculation && this.tour.lunchBreak && this.tour.lunchBreak.after_operation_idx != this.lunchBreakAfterOperationNumber - 1

    this.newTourTouched = !equal_operations || this.tourOperationsOrderViolateInsertionConfig(this.tour.operations) || !start_location_matches || !end_location_matches || lunch_break_changed

    if (this.newTourTouched) {

      let scored_routes = await this.calculatePossibleRoutes(update_ts, {start_location: use_start_location, end_location: use_end_location, operations: all_operations_used_in_new_tour})
      if (update_ts != this.latestNewOperationUpdateTimestamp) return

      let { optimized_route, used_scored_route } = await this.getOptimizedRoute(scored_routes, {start_location: use_start_location, end_location: use_end_location})
      if (update_ts != this.latestNewOperationUpdateTimestamp) return

      found_valid_route = optimized_route

      if (used_scored_route) {
        let unfitting_operations_before = this.changedTourContainer.unfittingOperations
        this.changedTourContainer.unfittingOperations = used_scored_route.removed_operations.map(o => o.clone());
        this.changedTourContainer.deletingOperations = deletion_operations;
        [...new Set(unfitting_operations_before.concat(this.changedTourContainer.unfittingOperations))].map(op => op.getStore().fireUpdateManually())
        this.changedTourContainer.unfittingOperations.concat(this.changedTourContainer.deletingOperations).map(op => {
          let config = this.insertionConfigMap.get(op.id)
          if (config) config.tour_index = -1
        })

      }

      if (found_valid_route) {
        this.freeTourIndexes = PAUtil.range(0, found_valid_route.insertions.length - 1)
      }

    } else {
      // COPY OLD TOUR
      found_valid_route = {
        errors: [],
        insertions: this.tour.operations.map(op =>
          {
            return {
              operation: op,
              start_time_ms: op.calculateOperationDayStartMilliseconds(),
              end_time_ms: op.calculateOperationDayEndMilliseconds(),
            }
          }
        ),
        lunch_break: this.tour.lunchBreak
      }
      this.changedTourContainer.unfittingOperations = []
      this.changedTourContainer.deletingOperations = [];
      this.freeTourIndexes = PAUtil.range(0, this.tour.operations.length - 1)
    }

    let new_tour = this.generateNewEmptyTour({ custom_start_location: use_start_location, custom_end_location: use_end_location })
    found_valid_route.lunch_break ? new_tour.addLunchBreak(found_valid_route.lunch_break.after_operation_idx, found_valid_route.lunch_break.duration_minutes) : {}

    let operations = this.tour.operations.concat(insertion_operations)

    let cloned_fixed_operations = operations.filter(operation => operation.date_travel_start).map(op => op.clone())
    let cloned_insert_operations = found_valid_route.insertions.map(i => i.operation.clone())
    for (let operation of cloned_fixed_operations.concat(cloned_insert_operations)) {
      operation.tour = new_tour
      operation.user_ids = [new_tour.technicianDate.technician.id]
    }
    for (let operation of cloned_insert_operations) {
      let date_time_ms = PATimeControl.Instance.dateToTimestamp(new Date(new_tour.technicianDate.day.utc_timestamp), true, false) + found_valid_route.insertions.find(i => i.operation.id == operation.id).start_time_ms
      operation.operation_date = PATimeControl.Instance.formatDate(new Date(date_time_ms))
    }

    let insert_operations = cloned_fixed_operations.concat(cloned_insert_operations)

    await new_tour.insertOperations(
      insert_operations,
      {
        wait_for_all_queued_updates: true,
        set_end_location_to_last_operation: set_end_location_to_last_operations_location
      }
    )

    const old_start_location = this.changedTourContainer.tour.route.start_location
    const old_end_location = this.changedTourContainer.tour.route.end_location
    const start_or_end_location_changed = old_start_location != new_tour.startLocation || old_end_location != new_tour.endLocation

    if (set_end_location_to_last_operations_location && new_tour.operations.length) {
      let marker: DraggableDestinationMarker = config?.custom_end_location != 'remove' ? config?.custom_end_location?.marker : null
      if (!marker) {
        let possible_tdc = PATourPlannerControl.Instance.tourPlannerOverviewTechnicianDateCollections.find(tdc => tdc.technicianDates.includes(this))
        if (possible_tdc) {
          possible_tdc.activateTravelTechnicianDateTimestamp(this.day.utc_timestamp)
          marker = possible_tdc.getMarkerForTimestampIfActive(this.day.utc_timestamp)
        }
      }
      if (marker) {
        marker.coordinates = new_tour.endLocation.coordinates
      }
    }

    let { equal_operation_times } = this.changedTourContainer.tour.compareRoute(new_tour.operations)
    if (config?.force_new_tour || (!equal_operation_times) || start_or_end_location_changed) {
      this.changedTourContainer.tour = new_tour
      this.fireUpdateManually('new_tour')
      PATourPlannerControl.Instance.updateTechnicianToursInPlanningProcess()
    }

    if (found_valid_route.errors.length) {
      let config: MatSnackBarConfig = {panelClass: 'error-snackbar', verticalPosition: 'top'}
      PATourPlannerControl.Instance.snackBar.open(
        `Optimierung: \n Achtung! Keine Route für ${this.technician.getFullName()} - ${this.day.date_string} möglich, die alle Beschränkungen erfüllt: \n ${found_valid_route.errors.join(' \n ')}`,
        'ok',
        config
      )
    }

    if (update_ts == this.latestNewOperationUpdateTimestamp) {
      this.latestNewOperationUpdateTimestamp = null
    }

  }

  private tourOperationsOrderViolateInsertionConfig(ordered_tour_operations: PAOperation[]): boolean {
    for (let operation of ordered_tour_operations) {
      let idx = ordered_tour_operations.indexOf(operation)
      let config = this.insertionConfigMap.get(operation.id)
      if (config.tour_index >= 0 && idx != config.tour_index) {
        return true
      }
    }
    return false
  }

  /**
   * HAUPTFUNKTION: Berechnet alle möglichen Routen-Permutationen und bewertet sie.
   *
   * ABLAUF:
   * 1. Aufträge kategorisieren (bereits gestartet, mit/ohne Ortswechsel, Fixtermine)
   * 2. Stores mit Ortswechsel permutieren (Fixtermine haben Vorrang beim 6er-Limit)
   * 3. Für jede Permutation: Fixtermine müssen in chronologischer Reihenfolge sein
   * 4. findTimeConstrainedPartialRoute prüft ob Route zeitlich machbar ist
   * 5. Gültige Routen bewerten (Distanz/Dauer) und zurückgeben
   *
   * RÜCKGABE: Array von ScoredRoute - jede enthält eine mögliche Route mit Bewertung
   */
  async calculatePossibleRoutes(update_ts: number, config: {start_location: PALocation, end_location: PALocation, operations: PAOperation[]}): Promise<ScoredRoute[]> {

    let operations = config.operations || []
    const use_start_location = config.start_location
    const use_end_location = config.end_location
    let permutation_input_limit = 6  // Max 6 Stores permutieren (6! = 720 Kombinationen)

    let store_operations_map = new Map<PAStore, PAOperation[]>()

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 1: Aufträge in Kategorien aufteilen
    // ═══════════════════════════════════════════════════════════════════════════

    // 1a) Bereits gestartete Aufträge - haben feste Position (Techniker ist schon unterwegs/vor Ort)
    //     Sortiert nach Startzeit, da die Reihenfolge feststeht
    let ordered_already_started_operations = operations.filter(operation => operation.date_travel_start).sort((operation_a, operation_b) => Date.parse(operation_a.date_travel_start) < Date.parse(operation_b.date_travel_start) ? -1 : 1)

    // 1b) Noch nicht gestartete Aufträge - können optimiert werden
    let not_started_operations = operations.filter(operation => !operation.date_travel_start)

    // 1c) Aufträge mit festem Tour-Index (User hat manuell Position festgelegt)
    //     Diese werden NICHT permutiert, sondern an ihrer festen Position eingefügt
    let not_started_operations_with_set_tour_idx = not_started_operations.filter(o => this.insertionConfigMap.has(o.id) && this.insertionConfigMap.get(o.id).tour_index >= 0)
    let ordered_operations_with_tour_idx = not_started_operations_with_set_tour_idx.sort((o1, o2) => {
      return this.insertionConfigMap.get(o1.id).tour_index < this.insertionConfigMap.get(o2.id).tour_index ? -1 : 1
    })

    // 1d) Aufträge die optimiert werden sollen (kein fester Tour-Index)
    let not_started_operations_to_optimize = not_started_operations.filter(o => !this.insertionConfigMap.has(o.id) || this.insertionConfigMap.get(o.id).tour_index == -1)

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 2: Aufträge nach Ortswechsel, Fixtermin und Tages-SLA kategorisieren
    // ═══════════════════════════════════════════════════════════════════════════

    // Hilfsfunktion: Prüft ob ein Auftrag ein Tages-SLA hat (SLA am gleichen Tag wie die geplante Tour)
    const hasSameDaySLA = (op: PAOperation): boolean => {
      if (!op.ticket.datesla) return false
      const sla_day_timestamp = PATimeControl.Instance.dateToTimestamp(new Date(op.ticket.datesla), true, true)
      return sla_day_timestamp === this.day.utc_timestamp
    }

    // Hilfsfunktion: Gibt die Prioritäts-Uhrzeit eines Auftrags zurück (für Sortierung)
    // Fixtermine: operation_date, Tages-SLA: datesla
    const getPriorityTime = (op: PAOperation): number => {
      if (op.date_fixed) {
        return new Date(op.operation_date).getTime()
      } else if (hasSameDaySLA(op)) {
        return new Date(op.ticket.datesla).getTime()
      }
      return Infinity  // Normale Aufträge haben keine Prioritäts-Zeit
    }

    // 2a) Fixtermine MIT Ortswechsel
    let fixed_date_operations_with_location_change = not_started_operations_to_optimize.filter(
      op => op.date_fixed && op.ticket.triggersLocationChange()
    )

    // 2b) Tages-SLA Aufträge MIT Ortswechsel (keine Fixtermine)
    //     SLA-Datum ist am gleichen Tag wie die geplante Tour
    let same_day_sla_operations_with_location_change = not_started_operations_to_optimize.filter(
      op => !op.date_fixed && op.ticket.triggersLocationChange() && hasSameDaySLA(op)
    )

    // 2c) Prioritäts-Aufträge = Fixtermine + Tages-SLA (beide gleichwertig, nach Uhrzeit sortiert)
    let priority_operations_with_location_change = [
      ...fixed_date_operations_with_location_change,
      ...same_day_sla_operations_with_location_change
    ].sort((op_a, op_b) => getPriorityTime(op_a) - getPriorityTime(op_b))

    // 2d) Fixtermine OHNE Ortswechsel → werden nicht permutiert (kein Einfluss auf Fahrzeit)
    let fixed_date_operations_without_location_change = not_started_operations_to_optimize.filter(
      op => op.date_fixed && !op.ticket.triggersLocationChange()
    )

    // 2e) Normale Aufträge MIT Ortswechsel (keine Fixtermine, kein Tages-SLA) → werden permutiert
    let normal_operations_with_location_change = not_started_operations_to_optimize.filter(
      op => !op.date_fixed && op.ticket.triggersLocationChange() && !hasSameDaySLA(op)
    )

    // 2f) Normale Aufträge OHNE Ortswechsel → werden nicht permutiert
    let normal_operations_without_location_change = not_started_operations_to_optimize.filter(
      op => !op.date_fixed && !op.ticket.triggersLocationChange()
    )

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 3: Store-zu-Operations Mapping erstellen
    // ═══════════════════════════════════════════════════════════════════════════
    // Mehrere Aufträge am gleichen Store werden zusammen behandelt (gleicher Ort)

    // Alle Aufträge mit Ortswechsel (Prioritäts-Aufträge + normale)
    let all_operations_with_location_change = [...priority_operations_with_location_change, ...normal_operations_with_location_change]
    for (let operation of all_operations_with_location_change) {
      let operations_store = operation.getStore()
      if (!store_operations_map.has(operations_store)) {
        store_operations_map.set(operations_store, [operation])
      } else {
        store_operations_map.get(operations_store).push(operation)
      }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 4: Stores für Permutation vorbereiten
    // ═══════════════════════════════════════════════════════════════════════════
    // Prioritäts-Aufträge (Fixtermine + Tages-SLA) haben Vorrang beim 6er-Limit

    // 4a) Prioritäts-Stores nach Zeit sortieren (früheste zuerst)
    //     Bei mehr als 6 werden die späteren abgelehnt
    let priority_stores_sorted = [...new Set(priority_operations_with_location_change.map(op => op.getStore()))]

    // 4b) Stores mit normalen Aufträgen
    let normal_stores = [...new Set(normal_operations_with_location_change.map(op => op.getStore()))]
    // Stores die schon bei Prioritäts-Aufträgen dabei sind, nicht doppelt zählen
    normal_stores = normal_stores.filter(store => !priority_stores_sorted.includes(store))

    // 4c) Limit anwenden: Prioritäts-Aufträge zuerst, dann normale Stores auffüllen
    let stores_to_permute: PAStore[] = []
    let stores_not_to_permute: PAStore[] = []
    let rejected_priority_operations: PAOperation[] = []  // Prioritäts-Aufträge die nicht eingeplant werden können

    if (priority_stores_sorted.length <= permutation_input_limit) {
      // Fall 1: Alle Prioritäts-Aufträge passen ins Limit
      stores_to_permute = [...priority_stores_sorted]
      // Restliche Plätze mit normalen Stores auffüllen
      let remaining_slots = permutation_input_limit - priority_stores_sorted.length
      stores_to_permute = stores_to_permute.concat(normal_stores.slice(0, remaining_slots))
      stores_not_to_permute = normal_stores.slice(remaining_slots)
    } else {
      // Fall 2: Mehr Prioritäts-Stores als Limit erlaubt
      // → Nur die ersten 6 (nach Zeit) permutieren, Rest als nicht einfügbar markieren
      stores_to_permute = priority_stores_sorted.slice(0, permutation_input_limit)
      let rejected_stores = priority_stores_sorted.slice(permutation_input_limit)
      stores_not_to_permute = [...normal_stores]  // Normale Stores werden nicht permutiert

      // Alle Prioritäts-Aufträge der abgelehnten Stores sammeln
      for (let rejected_store of rejected_stores) {
        let store_priority_ops = priority_operations_with_location_change.filter(op => op.getStore() === rejected_store)
        rejected_priority_operations = rejected_priority_operations.concat(store_priority_ops)
      }

      console.warn(`Mehr als ${permutation_input_limit} Prioritäts-Stores (${priority_stores_sorted.length}). ` +
        `${rejected_priority_operations.length} Fixtermin(e)/SLA-Auftrag/Aufträge können nicht eingeplant werden.`)
    }

    // 4d) Abgelehnte Prioritäts-Aufträge aus der Liste entfernen
    let accepted_priority_operations = priority_operations_with_location_change.filter(
      op => !rejected_priority_operations.includes(op)
    )

    // 4e) Nur akzeptierte FIXTERMINE für Reihenfolge-Validierung (SLA-Aufträge sind flexibler)
    let accepted_fixed_date_operations = accepted_priority_operations.filter(op => op.date_fixed)

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 5: Distance-Matrix vorladen (Performance-Optimierung)
    // ═══════════════════════════════════════════════════════════════════════════

    // Letzte bekannte Location vor den Permutationen (für Distanzberechnung)
    let last_fixed_location_before_permutations = getRoutesLastFixedLocation(ordered_already_started_operations) || use_start_location

    // Nur Locations der AKZEPTIERTEN Aufträge für die Matrix sammeln (MapBox-Requests sparen)
    let accepted_operations_with_location_change = all_operations_with_location_change.filter(
      op => !rejected_priority_operations.includes(op)
    )
    let all_locations_for_matrix = [...new Set(accepted_operations_with_location_change.map(op => op.ticket.location))]
    if (all_locations_for_matrix.length >= 1) {
      await PADataControl.Instance.distanceCalculator.preloadLocationDistanceMatrix(
        [last_fixed_location_before_permutations].concat(all_locations_for_matrix),
        all_locations_for_matrix.concat(use_end_location ? [use_end_location] : []),
        'min_credits'
      )
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 6: Alle Store-Permutationen generieren und bewerten
    // ═══════════════════════════════════════════════════════════════════════════

    let store_permutations = PAUtil.permutator<PAStore>(stores_to_permute)
    let scored_permutations: ScoredRoute[] = []
    let lowest_operation_remove_count = -1  // Tracking: Beste Route mit wenigsten entfernten Ops

    // Permutationen in Chunks verarbeiten (für bessere Performance und Abbruch-Prüfung)
    let permutation_chunks = PAUtil.chunkArray<PAStore[]>(store_permutations, 32)
    let invalid_permutation_count = 0

    for (let stores_chunk of permutation_chunks) {
      console.log(`starting chunk ${permutation_chunks.indexOf(stores_chunk)}`)
      // Abbruch wenn zwischenzeitlich neue Berechnung gestartet wurde
      if (update_ts != this.latestNewOperationUpdateTimestamp) return

      await Promise.all(stores_chunk.map(async store_permutation => {

        // 6a) Prüfen ob Fixtermine in dieser Permutation in chronologischer Reihenfolge sind
        //     Nur die akzeptierten Fixtermine prüfen (nicht die abgelehnten)
        if (!this.isValidFixedDateOrder(store_permutation, accepted_fixed_date_operations, store_operations_map)) {
          invalid_permutation_count += 1
          return  // Diese Permutation überspringen
        }

        // 6b) Route aus der Store-Permutation aufbauen
        let ordered_operations: PAOperation[] = []
        for (let store of store_permutation.concat(stores_not_to_permute)) {
          let store_operations = store_operations_map.get(store)
          if (store_operations) {
            ordered_operations = ordered_operations.concat(store_operations)
          }
        }

        // 6c) Bereits gestartete Aufträge VOR die permutierten setzen
        let route = ordered_already_started_operations.concat(ordered_operations)

        // 6d) Aufträge mit festem Tour-Index an ihrer Position einfügen
        for (let operation of ordered_operations_with_tour_idx) {
          let idx = this.insertionConfigMap.get(operation.id).tour_index
          route.splice(idx, 0, operation)
        }

        // 6e) Prüfen ob Route ins Zeitbudget passt (und ggf. Aufträge entfernen)
        let valid_partial_route = await this.findTimeConstrainedPartialRoute({
          route: route,
          start_location: use_start_location,
          end_location: use_end_location,
          extra_operations_without_location_change: normal_operations_without_location_change,
          fixed_date_operations_without_location_change: fixed_date_operations_without_location_change
        }, this.changedTourContainer.max_tour_minutes * 60 * 1000, lowest_operation_remove_count)

        // 6f) Wenn gültige Route gefunden: zur Liste hinzufügen
        if (valid_partial_route) {
          // Abgelehnte Fixtermine zu removed_operations hinzufügen
          let all_removed_operations = [...valid_partial_route.removed_operations, ...rejected_priority_operations]
          let removed_operation_count = all_removed_operations.length

          // Nur Routen behalten die mindestens so gut sind wie die beste bisherige
          if (lowest_operation_remove_count == -1 || removed_operation_count <= lowest_operation_remove_count) {
            lowest_operation_remove_count = all_removed_operations.length
            scored_permutations.push({
              route: valid_partial_route.partial_route,
              score: null,  // Wird später berechnet
              removed_operations: all_removed_operations,
              sorted_extra_operations_with_appointment_date: valid_partial_route.fixed_date_operations_without_location_change
            })
          } else {
            invalid_permutation_count += 1
          }
        } else {
          invalid_permutation_count += 1
        }
      }))
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 7: Gültige Routen bewerten (Score berechnen)
    // ═══════════════════════════════════════════════════════════════════════════

    if (update_ts != this.latestNewOperationUpdateTimestamp) return
    await Promise.all(scored_permutations.map(async store_permutation => {
      // Score = Gesamtdistanz und Gesamtdauer der Route
      store_permutation.score = await routeScore(store_permutation.route, use_start_location, use_end_location)
    }))

    return scored_permutations
  }

  /**
   * Prüft ob die Fixtermine in einer Store-Permutation in chronologischer Reihenfolge sind.
   * Fixtermine müssen in der Route in der gleichen Reihenfolge erscheinen wie ihre Uhrzeiten.
   */
  private isValidFixedDateOrder(
    store_permutation: PAStore[],
    sorted_fixed_date_operations: PAOperation[],
    store_operations_map: Map<PAStore, PAOperation[]>
  ): boolean {
    if (sorted_fixed_date_operations.length <= 1) return true  // 0 oder 1 Fixtermin → immer gültig

    // Finde die Position jedes Fixtermins in der Permutation
    let fixed_date_positions: {operation: PAOperation, position: number}[] = []

    for (let fixed_op of sorted_fixed_date_operations) {
      let op_store = fixed_op.getStore()
      let store_position = store_permutation.indexOf(op_store)
      if (store_position === -1) {
        // Store nicht in Permutation (wurde ans Ende gehängt) → Position = Infinity
        store_position = Infinity
      }
      fixed_date_positions.push({operation: fixed_op, position: store_position})
    }

    // Prüfen ob die Positionen aufsteigend sind (entsprechend der zeitlichen Sortierung)
    for (let i = 1; i < fixed_date_positions.length; i++) {
      if (fixed_date_positions[i].position < fixed_date_positions[i - 1].position) {
        return false  // Fixtermin i kommt VOR Fixtermin i-1, aber zeitlich ist i NACH i-1
      }
    }

    return true
  }

  /**
   * Prüft ob eine Route ins Zeitbudget passt und entfernt ggf. Aufträge.
   *
   * NEUE LOGIK (Hybrid-Ansatz):
   * - Fixtermine MIT Ortswechsel sind bereits in route enthalten (durch Permutation)
   * - Fixtermine OHNE Ortswechsel werden separat übergeben
   * - Zeitliche Simulation: Prüft ob Techniker rechtzeitig zu Fixterminen kommt
   *
   * PARAMETER:
   * - route_data.route: Die permutierte Route (inkl. Fixtermine mit Ortswechsel)
   * - route_data.extra_operations_without_location_change: Normale Ops ohne Ortswechsel
   * - route_data.fixed_date_operations_without_location_change: Fixtermine ohne Ortswechsel
   * - max_tour_time_ms: Maximale Tourzeit in Millisekunden
   * - highest_remove_count: Abbruch wenn mehr Aufträge entfernt werden müssten
   *
   * RÜCKGABE:
   * - partial_route: Die Route die ins Zeitbudget passt
   * - removed_operations: Aufträge die entfernt werden mussten
   * - fixed_date_operations_without_location_change: Fixtermine ohne Ortswechsel
   */
  async findTimeConstrainedPartialRoute(
    route_data: {
      start_location: PALocation;
      route: PAOperation[];
      end_location: PALocation;
      extra_operations_without_location_change: PAOperation[],
      fixed_date_operations_without_location_change: PAOperation[]
    },
    max_tour_time_ms: number,
    highest_remove_count: number
  ): Promise<{ partial_route: PAOperation[]; removed_operations: PAOperation[], fixed_date_operations_without_location_change: PAOperation[]} | null> {

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 1: Fixtermine ohne Ortswechsel nach Uhrzeit sortieren
    // ═══════════════════════════════════════════════════════════════════════════

    let fixed_date_ops_no_location = [...route_data.fixed_date_operations_without_location_change].sort((op_a, op_b) => {
      return new Date(op_a.operation_date).getTime() - new Date(op_b.operation_date).getTime()
    })

    // Aufträge ohne Ortswechsel (und keine Fixtermine)
    let extra_ops_no_location = [...route_data.extra_operations_without_location_change]
    let operation_time_map = new Map<PAOperation, number>()

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 2: Route mit Extra-Aufträge (ohne Ortswechsel) zusammenbauen
    // ═══════════════════════════════════════════════════════════════════════════

    let route_with_extra_operations: PAOperation[] = []
    for (let operation of route_data.route) {
      let op_tour_idx = this.insertionConfigMap.get(operation.id)?.tour_index
      if (op_tour_idx != -1) {
        let diff = op_tour_idx - route_data.route.indexOf(operation)
        while (diff > 0 && extra_ops_no_location.length) {
          route_with_extra_operations.push(extra_ops_no_location.splice(0, 1)[0])
          diff -= 1
        }
      }
      route_with_extra_operations.push(operation)
    }
    route_with_extra_operations = route_with_extra_operations.concat(extra_ops_no_location)

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 3: Arbeitszeiten für alle Aufträge berechnen
    // ═══════════════════════════════════════════════════════════════════════════

    let all_operations = [...route_with_extra_operations, ...fixed_date_ops_no_location]
    let priority_idxs = await this.technician.currentTourPlannings.getRoutesPriorityOperationCountsOnDay(all_operations, this.day.utc_timestamp)
    for (let operation of all_operations) {
      let priority_entry = priority_idxs.find(route_idxs => route_idxs.operation == operation)
      let operation_time = operation.calculateOperationTimeMilliseconds({
        use_technician: this.technician,
        use_priority_idx: priority_entry ? priority_entry.count : 0
      })
      operation_time_map.set(operation, operation_time)
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 4: Zeitliche Simulation der Route
    // ═══════════════════════════════════════════════════════════════════════════

    let removed_operations: PAOperation[] = []
    let current_partial_route = route_with_extra_operations

    while ((current_partial_route.length || fixed_date_ops_no_location.length) && (highest_remove_count == -1 || removed_operations.length <= highest_remove_count)) {

      // 4a) Zeitliche Simulation durchführen
      const simulation_result = await this.simulateRouteTime(
        current_partial_route,
        fixed_date_ops_no_location,
        route_data.start_location,
        route_data.end_location,
        operation_time_map
      )

      // 4b) Prüfen ob Route zeitlich machbar ist
      if (!simulation_result.is_valid) {
        // Ein Fixtermin kann nicht erreicht werden → diese Permutation ist ungültig
        return null
      }

      // 4c) Prüfen ob Zeitbudget eingehalten wird
      if (simulation_result.total_time_ms < max_tour_time_ms) {
        // Route passt ins Budget
        return {
          partial_route: current_partial_route,
          removed_operations,
          fixed_date_operations_without_location_change: fixed_date_ops_no_location
        }
      }

      // 4d) Route zu lang → letzte entfernbare Operation finden und entfernen
      let last_removable_operation: PAOperation = null
      for (let operation of current_partial_route) {
        // Fixtermine sind NICHT entfernbar (müssen an diesem Tag erledigt werden)
        if (!operation.date_fixed && this.insertionConfigMap.has(operation.id) && this.insertionConfigMap.get(operation.id).removable) {
          last_removable_operation = operation
        }
      }

      if (!last_removable_operation) {
        // Keine entfernbare Operation mehr → Route so zurückgeben wie sie ist
        return {
          partial_route: current_partial_route,
          removed_operations,
          fixed_date_operations_without_location_change: fixed_date_ops_no_location
        }
      }

      // Operation entfernen
      let last_removed_idx = current_partial_route.indexOf(last_removable_operation)
      current_partial_route = current_partial_route.filter(op => op !== last_removable_operation)

      // Nach Entfernung: Aufträge mit festem tour_index ggf. verschieben
      if (current_partial_route.length !== last_removed_idx) {
        let all_spliced_ops: {op: PAOperation, idx: number}[] = []
        for (let idx of PAUtil.range(last_removed_idx, current_partial_route.length - 1)) {
          let config = this.insertionConfigMap.get(current_partial_route[idx - all_spliced_ops.length]?.id)
          if (config && config.tour_index !== -1) {
            all_spliced_ops.push({op: current_partial_route.splice(idx - all_spliced_ops.length, 1)[0], idx: idx})
          }
        }
        for (let spliced_op of all_spliced_ops) {
          current_partial_route.splice(spliced_op.idx + 1, 0, spliced_op.op)
        }
      }

      removed_operations.push(last_removable_operation)
    }

    // Abbruch wenn zu viele Aufträge entfernt werden müssten
    if (highest_remove_count !== -1 && removed_operations.length > highest_remove_count) {
      return null
    }

    return {
      partial_route: current_partial_route,
      removed_operations,
      fixed_date_operations_without_location_change: fixed_date_ops_no_location
    }
  }

  /**
   * Simuliert den zeitlichen Ablauf einer Route.
   * Prüft ob der Techniker rechtzeitig zu allen Fixterminen kommt.
   *
   * ABLAUF:
   * - Startet um 8:00 Uhr (oder früher wenn nötig für ersten Fixtermin)
   * - Iteriert durch die Route in Array-Reihenfolge
   * - Bei Fixterminen: Prüft ob Ankunftszeit <= Fixtermin-Uhrzeit
   * - Bei normalen Ops: Addiert Fahrzeit + Arbeitszeit
   * - Fixtermine ohne Ortswechsel werden zeitlich eingeschoben
   */
  private async simulateRouteTime(
    route: PAOperation[],
    fixed_date_ops_no_location: PAOperation[],
    start_location: PALocation,
    end_location: PALocation,
    operation_time_map: Map<PAOperation, number>
  ): Promise<{is_valid: boolean, total_time_ms: number}> {

    const configured_start_ms = PASettingsControl.Instance.getWorkStartMsForTechnician(this.technician.id)
    const is_arrival_time = PASettingsControl.Instance.getIsArrivalTimeForTechnician(this.technician.id)

    // Bei Ankunftszeit: Losfahrzeit = konfigurierte Zeit - Fahrzeit zum ersten Auftrag
    let day_start_ms = configured_start_ms
    if (is_arrival_time && route.length > 0) {
      const first_op = route[0]
      if (first_op.ticket.triggersLocationChange()) {
        const driving_minutes = (await start_location.getDistanceToLocation(first_op.ticket.location, true)).duration
        const driving_time_ms = Math.round(driving_minutes * this.technician.drivingTimeFactor) * 60 * 1000
        day_start_ms = configured_start_ms - driving_time_ms
      }
    }

    let current_time_ms = day_start_ms
    let total_work_and_drive_time_ms = 0
    let current_location = start_location

    // Kopie der Fixtermine ohne Ortswechsel (werden während Simulation "verbraucht")
    let remaining_fixed_no_location = [...fixed_date_ops_no_location]

    for (let operation of route) {

      // Fahrzeit zur nächsten Operation berechnen (falls Ortswechsel)
      let driving_time_ms = 0
      if (operation.ticket.triggersLocationChange()) {
        let driving_minutes = (await current_location.getDistanceToLocation(operation.ticket.location, true)).duration
        driving_time_ms = Math.round(driving_minutes * this.technician.drivingTimeFactor) * 60 * 1000
      }

      // Vor dieser Operation: Fixtermine ohne Ortswechsel einfügen die zeitlich dazwischen liegen
      let arrival_time_ms = current_time_ms + driving_time_ms
      for (let fixed_op of [...remaining_fixed_no_location]) {
        let fixed_time_ms = PATimeControl.Instance.dateTimeToDayMinutes(fixed_op.operation_date) * 60 * 1000
        let op_work_time_ms = operation_time_map.get(operation) || 0

        // Wenn der Fixtermin VOR dem Ende der aktuellen Operation liegt
        if (fixed_time_ms < arrival_time_ms + op_work_time_ms) {
          // Fixtermin einschieben
          if (current_time_ms > fixed_time_ms) {
            // Techniker kann den Fixtermin nicht rechtzeitig erreichen
            // (Bei Ops ohne Ortswechsel ist das aber nicht so kritisch - er ist ja schon da)
          }
          // Wartezeit bis Fixtermin (falls früher angekommen)
          if (current_time_ms < fixed_time_ms) {
            current_time_ms = fixed_time_ms
          }
          // Arbeitszeit des Fixtermins addieren
          let fixed_work_time_ms = operation_time_map.get(fixed_op) || 0
          current_time_ms += fixed_work_time_ms
          total_work_and_drive_time_ms += fixed_work_time_ms

          remaining_fixed_no_location = remaining_fixed_no_location.filter(op => op !== fixed_op)
        }
      }

      // Jetzt die eigentliche Operation verarbeiten
      if (operation.date_fixed) {
        // FIXTERMIN MIT ORTSWECHSEL
        let fixed_time_ms = PATimeControl.Instance.dateTimeToDayMinutes(operation.operation_date) * 60 * 1000
        let arrival_time = current_time_ms + driving_time_ms

        if (arrival_time > fixed_time_ms) {
          // Techniker kommt zu spät zum Fixtermin → Route ungültig
          return {is_valid: false, total_time_ms: 0}
        }

        // Wartezeit bis Fixtermin
        current_time_ms = fixed_time_ms
        total_work_and_drive_time_ms += driving_time_ms

      } else {
        // NORMALE OPERATION
        current_time_ms += driving_time_ms
        total_work_and_drive_time_ms += driving_time_ms
      }

      // Arbeitszeit addieren
      let work_time_ms = operation_time_map.get(operation) || 0
      current_time_ms += work_time_ms
      total_work_and_drive_time_ms += work_time_ms

      // Location aktualisieren
      if (operation.ticket.triggersLocationChange()) {
        current_location = operation.ticket.location
      }
    }

    // Restliche Fixtermine ohne Ortswechsel am Ende
    for (let fixed_op of remaining_fixed_no_location) {
      let fixed_time_ms = PATimeControl.Instance.dateTimeToDayMinutes(fixed_op.operation_date) * 60 * 1000
      if (current_time_ms < fixed_time_ms) {
        current_time_ms = fixed_time_ms
      }
      let work_time_ms = operation_time_map.get(fixed_op) || 0
      current_time_ms += work_time_ms
      total_work_and_drive_time_ms += work_time_ms
    }

    // Heimfahrt
    if (end_location) {
      let home_driving_minutes = (await current_location.getDistanceToLocation(end_location, true)).duration
      let home_driving_ms = Math.round(home_driving_minutes * this.technician.drivingTimeFactor) * 60 * 1000
      total_work_and_drive_time_ms += home_driving_ms
    }

    return {is_valid: true, total_time_ms: total_work_and_drive_time_ms}
  }

  async getOptimizedRoute(scored_routes: ScoredRoute[], config: {start_location: PALocation, end_location?: PALocation}): Promise<{ optimized_route: OptimizedRoute, used_scored_route: ScoredRoute }> {

    let sorted_routes = sortScoredRoutes(scored_routes)
    let optimized_unconstrained_route: OptimizedRoute
    let optimized_constrained_route: OptimizedRoute
    let used_scored_route: ScoredRoute

    for (let scored_route of sorted_routes) {

      const route = scored_route.route

      if (!route.length && !scored_route.sorted_extra_operations_with_appointment_date.length) {
        optimized_constrained_route = optimized_unconstrained_route = {insertions: [], errors: []}
        used_scored_route = scored_route
        break
      }

      let ts_now = Date.now()
      let route_priority_idxs = await this.technician.currentTourPlannings.getRoutesPriorityOperationCountsOnDay(route, this.day.utc_timestamp)

      let insertions: Insertion[] = []
      let already_started_operations = route.filter(operation => operation.date_travel_start)

      let next_earliest_driving_start_ms: number
      let last_started_location: PALocation

      if (already_started_operations.length) {
        let last_operation = already_started_operations[already_started_operations.length - 1]
        let operation_time_data = await last_operation.getOperationTimeData(this.technician, 0)

        if (PAFilterControl.Instance.selectedOperationTimeFilter == 'planned') {
          next_earliest_driving_start_ms = operation_time_data.estimated_end_time * 60 * 1000
        } else {
          next_earliest_driving_start_ms = operation_time_data.average_end_time * 60 * 1000
        }
        last_started_location = getRoutesLastFixedLocation(already_started_operations) || config.start_location
      } else {
        let first_operation = route[0]
        last_started_location = config.start_location

        if (first_operation?.date_fixed) {
          // Fixtermin: Feste Uhrzeit verwenden, nicht die Öffnungszeit
          let fixed_time_day_minutes = PATimeControl.Instance.dateTimeToDayMinutes(first_operation.operation_date)
          let driving_time_minutes = first_operation.ticket.triggersLocationChange()
            ? (await last_started_location.getDistanceToLocation(first_operation.ticket.location)).duration
            : 0
          let technician_driving_time_ms = Math.round(driving_time_minutes * this.technician.drivingTimeFactor) * 60 * 1000
          next_earliest_driving_start_ms = fixed_time_day_minutes * 60 * 1000 - technician_driving_time_ms
        } else {
          // Normaler Auftrag: Techniker-Startzeit als Basis, ggf. nach hinten verschieben für Store-Öffnung
          const configured_start_ms = PASettingsControl.Instance.getWorkStartMsForTechnician(this.technician.id)
          const is_arrival_time = PASettingsControl.Instance.getIsArrivalTimeForTechnician(this.technician.id)

          if (first_operation?.ticket.triggersLocationChange()) {
            let driving_time_minutes = (await last_started_location.getDistanceToLocation(first_operation.ticket.location)).duration
            let technician_driving_time_ms = Math.round(driving_time_minutes * this.technician.drivingTimeFactor) * 60 * 1000

            // Bei Ankunftszeit: Losfahrzeit = Ankunftszeit - Fahrzeit
            // Bei Losfahrzeit: Losfahrzeit = konfigurierte Zeit
            let technician_departure_ms = is_arrival_time
              ? configured_start_ms - technician_driving_time_ms
              : configured_start_ms
            let arrival_at_store_ms = is_arrival_time
              ? configured_start_ms
              : configured_start_ms + technician_driving_time_ms

            // Prüfe Store-Öffnungszeit
            let first_operations_opening_time = first_operation.ticket.store_openings[PATimeControl.Instance.getTimeStampsWeekDay(ts_now)]?.open
            let opening_time_ms = first_operations_opening_time
              ? PATimeControl.Instance.timeStringToMinutes(first_operations_opening_time) * 60 * 1000
              : undefined

            if (opening_time_ms && arrival_at_store_ms < opening_time_ms) {
              // Techniker würde VOR Öffnung ankommen → Start nach hinten verschieben
              // sodass er genau zur Öffnung ankommt
              next_earliest_driving_start_ms = opening_time_ms - technician_driving_time_ms
            } else {
              next_earliest_driving_start_ms = technician_departure_ms
            }
          } else {
            // Kein Ortswechsel: Bei Ankunftszeit und Losfahrzeit identisch (keine Fahrzeit)
            next_earliest_driving_start_ms = configured_start_ms
          }
        }
      }

      if (this.day.utc_timestamp < ts_now && ts_now < this.day.utc_timestamp + 24 * 60 * 60 * 1000) {
        let ms_since_day_start = ts_now - PATimeControl.Instance.dateToTimestamp(new Date(), true, false)
        if (ms_since_day_start > next_earliest_driving_start_ms) {
          next_earliest_driving_start_ms = ms_since_day_start
        }
      }

      let already_started_operation_times = await Promise.all(already_started_operations.map(
        async op => {
          let op_time_data = await op.getOperationTimeData(this.technician, 0)
          return op_time_data.driving_time + PAFilterControl.Instance.selectedOperationTimeFilter == 'planned' ? op_time_data.estimated_worktime : op_time_data.average_worktime
        }
      ))

      let not_started_operations = route.filter(operation => !operation.date_travel_start)
      let last_tour_location = getRoutesLastFixedLocation(not_started_operations) || last_started_location
      let last_city_before_insertions = last_tour_location.city
      let home_driving_minutes = !config.end_location ? 0 : (await last_tour_location.getDistanceToLocation(config.end_location)).duration
      let technician_home_driving_ms = Math.round(home_driving_minutes * this.technician.drivingTimeFactor) * 60 * 1000
      let current_tour_time_ms = already_started_operation_times.reduce((sum, time) => sum + time * 60 * 1000, 0)
      let fill_tour_with_appointment_date_operations = [...scored_route.sorted_extra_operations_with_appointment_date]

      // ═══════════════════════════════════════════════════════════════════════════
      // OPTIMIERUNG: Aufträge VOR dem ersten Fixtermin an diesen ranziehen
      // ═══════════════════════════════════════════════════════════════════════════
      // Damit keine unnötige Pause zwischen den ersten Aufträgen und dem Fixtermin entsteht
      // ABER: SLA-Aufträge dürfen nicht später als ihre SLA-Uhrzeit starten!

      // Hilfsfunktion: Prüft ob ein Auftrag ein Tages-SLA hat
      const hasSameDaySLA = (op: PAOperation): boolean => {
        if (!op.ticket.datesla) return false
        const sla_day_timestamp = PATimeControl.Instance.dateToTimestamp(new Date(op.ticket.datesla), true, true)
        return sla_day_timestamp === this.day.utc_timestamp
      }

      let first_fixed_date_operation = not_started_operations.find(op => op.date_fixed)
      if (first_fixed_date_operation && not_started_operations.indexOf(first_fixed_date_operation) > 0) {
        // Es gibt einen Fixtermin und es gibt Aufträge davor
        let first_fixed_idx = not_started_operations.indexOf(first_fixed_date_operation)
        let operations_before_fixed = not_started_operations.slice(0, first_fixed_idx)

        // Rückwärts berechnen: Wie viel Zeit brauchen alle Aufträge vor dem Fixtermin?
        let total_time_before_fixed_ms = 0
        let calc_location = config.start_location

        for (let op of operations_before_fixed) {
          // Fahrzeit
          if (op.ticket.triggersLocationChange()) {
            let driving_minutes = (await calc_location.getDistanceToLocation(op.ticket.location)).duration
            total_time_before_fixed_ms += Math.round(driving_minutes * this.technician.drivingTimeFactor) * 60 * 1000
            calc_location = op.ticket.location
          }
          // Arbeitszeit
          let priority_entry = route_priority_idxs.find(route_idxs => route_idxs.operation == op)
          let work_time_ms = op.calculateOperationTimeMilliseconds({
            use_technician: this.technician,
            use_priority_idx: priority_entry ? priority_entry.count : 0
          })
          total_time_before_fixed_ms += work_time_ms
        }

        // Fahrzeit zum Fixtermin
        if (first_fixed_date_operation.ticket.triggersLocationChange()) {
          let driving_to_fixed_minutes = (await calc_location.getDistanceToLocation(first_fixed_date_operation.ticket.location)).duration
          total_time_before_fixed_ms += Math.round(driving_to_fixed_minutes * this.technician.drivingTimeFactor) * 60 * 1000
        }

        // Optimale Startzeit: Fixtermin-Zeit minus Gesamtzeit davor
        let fixed_time_ms = PATimeControl.Instance.dateTimeToDayMinutes(first_fixed_date_operation.operation_date) * 60 * 1000
        let optimal_start_ms = fixed_time_ms - total_time_before_fixed_ms

        // SLA-Begrenzung: Prüfen ob ein Auftrag vor dem Fixtermin ein Tages-SLA hat
        // Die optimale Startzeit darf nicht dazu führen, dass ein SLA-Auftrag zu spät fertig wird
        let cumulative_time_ms = 0
        calc_location = config.start_location

        for (let op of operations_before_fixed) {
          // Fahrzeit zu diesem Auftrag
          let driving_time_ms = 0
          if (op.ticket.triggersLocationChange()) {
            let driving_minutes = (await calc_location.getDistanceToLocation(op.ticket.location)).duration
            driving_time_ms = Math.round(driving_minutes * this.technician.drivingTimeFactor) * 60 * 1000
            calc_location = op.ticket.location
          }

          // Arbeitszeit dieses Auftrags
          let priority_entry = route_priority_idxs.find(route_idxs => route_idxs.operation == op)
          let work_time_ms = op.calculateOperationTimeMilliseconds({
            use_technician: this.technician,
            use_priority_idx: priority_entry ? priority_entry.count : 0
          })

          // Wenn dieser Auftrag ein Tages-SLA hat: Prüfen ob er rechtzeitig fertig wird
          if (hasSameDaySLA(op)) {
            let sla_time_ms = PATimeControl.Instance.dateTimeToDayMinutes(op.ticket.datesla) * 60 * 1000
            // Endzeit dieses Auftrags bei der optimalen Startzeit
            let op_end_time_ms = optimal_start_ms + cumulative_time_ms + driving_time_ms + work_time_ms

            if (op_end_time_ms > sla_time_ms) {
              // Auftrag würde nach SLA fertig werden → Startzeit früher setzen
              let required_start_ms = sla_time_ms - cumulative_time_ms - driving_time_ms - work_time_ms
              if (required_start_ms < optimal_start_ms) {
                optimal_start_ms = required_start_ms
              }
            }
          }

          cumulative_time_ms += driving_time_ms + work_time_ms
        }

        // Nur ranziehen wenn die optimale Startzeit SPÄTER ist als die aktuelle
        // (wir wollen nicht früher starten als nötig)
        if (optimal_start_ms > next_earliest_driving_start_ms) {
          next_earliest_driving_start_ms = optimal_start_ms
        }
      }

      for (let operation of not_started_operations) {

        const current_ticket = operation.ticket
        let technician_driving_time_ms = 0
        if (current_ticket.triggersLocationChange()) {
          let current_location = current_ticket.client.location
          let driving_time_minutes = (await last_started_location.getDistanceToLocation(current_location)).duration
          technician_driving_time_ms = Math.round(driving_time_minutes * this.technician.drivingTimeFactor) * 60 * 1000
          last_started_location = current_location
        }
        let operation_start_ms = next_earliest_driving_start_ms + technician_driving_time_ms

        const forwarded_appointment_operations: PAOperation[] = []
        for (let appointment_operation of fill_tour_with_appointment_date_operations) {
          let app_day_minutes = PATimeControl.Instance.dateTimeToDayMinutes(appointment_operation.operation_date)
          if (!operation.finishesBeforeDayMinutes(app_day_minutes, {
            start_operation_at_day_minutes: operation_start_ms / (60 * 1000),
            use_technician: this.technician,
            use_priority_idx: route_priority_idxs.find(route_idxs => route_idxs.operation == operation).count
          })) {
            forwarded_appointment_operations.push(appointment_operation)
            const app_start_time_ms = app_day_minutes * 60 * 1000
            const app_operation_time_ms = appointment_operation.calculateOperationTimeMilliseconds({use_technician: this.technician})
            const app_end_time_ms = app_start_time_ms + app_operation_time_ms
            insertions.push({operation: appointment_operation, start_time_ms: app_start_time_ms, tour_time_ms: current_tour_time_ms, end_time_ms: app_end_time_ms})

            if (app_end_time_ms > next_earliest_driving_start_ms) next_earliest_driving_start_ms = app_end_time_ms
            current_tour_time_ms += app_operation_time_ms
          }
        }
        fill_tour_with_appointment_date_operations = fill_tour_with_appointment_date_operations.filter(app_op => !forwarded_appointment_operations.includes(app_op))

        // Bei Fixterminen: Feste Uhrzeit verwenden, nicht die nächste freie Zeit
        if (operation.date_fixed) {
          let fixed_time_day_minutes = PATimeControl.Instance.dateTimeToDayMinutes(operation.operation_date)
          operation_start_ms = fixed_time_day_minutes * 60 * 1000
        } else {
          operation_start_ms = next_earliest_driving_start_ms + technician_driving_time_ms
        }

        let operation_time_ms = operation.calculateOperationTimeMilliseconds({
          use_technician: this.technician,
          use_priority_idx: route_priority_idxs.find(route_idxs => route_idxs.operation == operation).count
        })
        next_earliest_driving_start_ms = operation_start_ms + operation_time_ms
        insertions.push({operation: operation, start_time_ms: operation_start_ms, tour_time_ms: current_tour_time_ms, end_time_ms: next_earliest_driving_start_ms})

        current_tour_time_ms += technician_driving_time_ms + operation_time_ms
      }

      for (let operation of fill_tour_with_appointment_date_operations) {
        let app_day_minutes = PATimeControl.Instance.dateTimeToDayMinutes(operation.operation_date)
        const app_start_time_ms = app_day_minutes * 60 * 1000
        const app_operation_time_ms = operation.calculateOperationTimeMilliseconds({use_technician: this.technician})
        const app_end_time_ms = app_start_time_ms + app_operation_time_ms
        insertions.push({operation: operation, start_time_ms: app_start_time_ms, tour_time_ms: current_tour_time_ms, end_time_ms: app_end_time_ms})
      }

      //add lunch break
      let lunch_break: { after_operation_idx: number, duration_minutes: number }
      let lunch_config = PATour.getLunchBreakTimeConfig((current_tour_time_ms + technician_home_driving_ms) / 60000)
      if (lunch_config) {
        let shift_start_insertion: Insertion

        const has_invalid_lunch_break_idx_config = this.lunchBreakAfterOperationNumber > insertions.length - 1
        if (has_invalid_lunch_break_idx_config) {
          this.automaticLunchBreakCalculation = true
          this.lunchBreakAfterOperationNumber = null
        }

        if (this.lunchBreakAfterOperationNumber) {
          shift_start_insertion = insertions[this.lunchBreakAfterOperationNumber]
        } else {
          shift_start_insertion = insertions.find(insertion => insertion.tour_time_ms > lunch_config.after_tour_minute * 60000 && route.indexOf(insertion.operation) != 0)
          if (!shift_start_insertion && insertions.length && route.length > 1) {
            // If no valid operation found, take the last insertion if the tour has at least 2 operations
            shift_start_insertion = insertions[insertions.length - 1]
          }
        }

        if (shift_start_insertion) {
          let shift_start_idx = insertions.indexOf(shift_start_insertion)
          lunch_break = {
            after_operation_idx: shift_start_idx - 1,
            duration_minutes: lunch_config.minutes
          }
          shiftInsertionsByMinutes(insertions, shift_start_idx, lunch_config.minutes);
        } else {
          lunch_break = {
            after_operation_idx: this.lunchBreakAfterOperationNumber || 0,
            duration_minutes: lunch_config.minutes
          }
        }
      }

      let { errors } = this.insertionsMatchConstraints(insertions, last_city_before_insertions)

      if (sorted_routes.indexOf(scored_route) == 0) {
        optimized_unconstrained_route = {
          errors: errors,
          insertions: insertions,
          lunch_break: lunch_break
        }
        used_scored_route = scored_route
      }

      if (!errors.length) {
        optimized_constrained_route = {
          errors: errors,
          insertions: insertions,
          lunch_break: lunch_break
        }
        used_scored_route = scored_route
        break
      }
    }

    if (!optimized_constrained_route) console.log(optimized_unconstrained_route.errors.join(', '))

    return { optimized_route: optimized_constrained_route || optimized_unconstrained_route, used_scored_route }
  }

  public updateInsertionConfigMap(insertion_operations: PAOperation[]) {
    let all_operations: PAOperation[]

    let tour_operations = this.tour.operations
    if (tour_operations) {
      for (let operation of tour_operations) {
        if (!this.insertionConfigMap.has(operation.id)) {
          const tour_idx = operation.hasStarted() ? tour_operations.indexOf(operation) : -1
          this.insertionConfigMap.set(operation.id, { removable: false, tour_index: tour_idx })
        }
      }
    }

    for (let operation of insertion_operations) {
      if (!this.insertionConfigMap.has(operation.id)) {
        let is_main_operation_to_plan = PATourPlannerControl.Instance.tourPlannerPlanningMode == 'operation' && PATourPlannerControl.Instance.operationToPlanID == operation.id
        this.insertionConfigMap.set(operation.id, {removable: !is_main_operation_to_plan, tour_index: -1})
      }
    }

    all_operations = tour_operations.concat(insertion_operations)
    for (let key of this.insertionConfigMap.keys()) {
      if (!all_operations.find(op => key == op.id)) {
        this.insertionConfigMap.delete(key)
      }
    }
  }

  removeOperationsFromNewTour(remove_operations_ids: number[]): void {
    let insert_operations = this.newTourConfig?.insert_operations || []
    const remove_insert_operations = insert_operations.filter(ins_op => remove_operations_ids.find(rem_op_id => rem_op_id == ins_op.id))
    const delete_operations = this.tour.operations.filter(
      tour_op => !tour_op.hasStarted() && remove_operations_ids.find(rem_op_id => rem_op_id == tour_op.id)
    )
    if (remove_insert_operations.length) {
      let timestamps = PATourPlannerControl.Instance.tourPlannerPlanningMode == 'technician' ? [this.day.utc_timestamp] : null
      PATourPlannerControl.Instance.planningInstructions.removeOperationsTechnicianConstraints(remove_insert_operations, [this.technician], timestamps)
      PATourPlannerControl.Instance.removeExtraOperationsIfNotUsed(remove_insert_operations)
    }
    if (delete_operations.length) {
      let changes_before = this.newTourConfig
      this.updateNewTourConfig({
        delete_operations: (changes_before.delete_operations || []).concat(delete_operations)
      })
    }
  }

  restoreOperationToNewTour(add_operation: PAOperation): void {
    let changes_before = this.newTourConfig
    let deleted_operations_before = changes_before?.delete_operations || []
    let deleted_operation = deleted_operations_before.find(operation => operation.id == add_operation.id)
    if (deleted_operation) {
      this.updateNewTourConfig({
        delete_operations: deleted_operations_before.filter(op => op.id != add_operation.id)
      })
    }
  }

  insertionConfigIndexIsDisabled(idx: number): boolean {
    return !![...this.insertionConfigMap].find(i_config_kv => !this.changedTourContainer.unfittingOperations.find(op => op.id == i_config_kv[0]) && i_config_kv[1].tour_index == idx)
  }

  updateNewTourConfig(config?: NewTourConfig): void {
    if (!this.isPastTechnicianDate) {
      let delete_operations_before = this.newTourConfig?.delete_operations || []
      let insert_operations_before = this.newTourConfig?.insert_operations || []
      let custom_start_before = this.newTourConfig?.custom_start_location || null
      let custom_end_before = this.newTourConfig?.custom_end_location || null

      this._newTourConfig = {
        force_new_tour: config?.force_new_tour,
        insert_operations: config?.insert_operations || insert_operations_before,
        delete_operations: config?.delete_operations || delete_operations_before,
        custom_start_location: typeof config?.custom_start_location == 'undefined' ? custom_start_before : config.custom_start_location,
        custom_end_location: typeof config?.custom_end_location == 'undefined' ?  custom_end_before : config.custom_end_location,
      }

      this.updateNewTour(this._newTourConfig).then(
        _ => {
          //this.selectTimelineTourForMap(changes.skip_map_center)
        }
      )
    }
  }

  async checkCurrentTourForDBChanges(): Promise<DBOperationChange[]> {
    return await this.checkTourForDBChanges(this.tour)
  }

  async checkNewTourForDBChanges(): Promise<DBOperationChange[]> {
    return await this.checkTourForDBChanges(this.changedTourContainer.tour)
  }

  async checkTourForDBChanges(tour: PATour): Promise<DBOperationChange[]> {
    let new_tour_operations = tour.operations
    let check_operations = new_tour_operations.filter(op => op.id > 0)

    if (!check_operations.length) return []

    return await new Promise<DBOperationChange[]>(
      resolve => {
        PAOperation.planningAssistantService.getPlanningAssistantOperations(check_operations.map(op => op.id)).subscribe(
          data => {
            let res: DBOperationChange[] = []
            for (let operation_hash of data) {
              let new_tour_base_operation = PADataControl.Instance.getOperation(operation_hash.id, null)
              let user_ids_changed: OperationUIDChanged = new_tour_base_operation.user_ids[0] != operation_hash.user_ids[0] ? {current_uid: new_tour_base_operation.user_ids[0], db_uid: operation_hash.user_ids[0]} : null
              let operation_date_changed: OperationDateChanged = new_tour_base_operation.operation_date != operation_hash.operation_date ? {current_operation_date: new_tour_base_operation.operation_date, db_operation_date: operation_hash.operation_date} : null
              if (user_ids_changed || operation_date_changed) {
                if (!user_ids_changed) {
                  res.push({base_operation: new_tour_base_operation, date_changed: operation_date_changed})
                } else if (!operation_date_changed) {
                  res.push({base_operation: new_tour_base_operation, uid_changed: user_ids_changed})
                } else {
                  res.push({base_operation: new_tour_base_operation, uid_changed: user_ids_changed, date_changed: operation_date_changed})
                }
              }
            }
            resolve(res)
          },
          err => {
            console.log(err)
          }
        )
      }
    )
  }

  getConstrainedOperations(operations: PAOperation[], planning_instructions: PAPlanningInstructions): PAOperation[] {
    return operations.filter(op => planning_instructions.operationIsDoableOnTechnicianDate(op, this))
  }

  static setTravelTechnicianDateService(service: TravelTechnicianDateService): void {
    this.travelTechnicianDateService = service
  }

  static async getTravelTechnicianDate(uid: number, timestamp: number): Promise<TravelTechnicianDate> {
    if (!PATechnicianDate.travelTechnicianDates.has(timestamp)) {
      const cw = PATimeControl.Instance.timestampToCalendarWeek(timestamp)
      await this.loadTravelTechnicianDatesForCalendarWeek(cw)
    }

    await PAUtil.sleepUntilConditionBreaks(
      () => {
        return PATechnicianDate.travelTechnicianDates.get(timestamp) == 'loading'
      }
    )

    let travel_technician_date_user_map = PATechnicianDate.travelTechnicianDates.get(timestamp) as Map<number, TravelTechnicianDate>
    if (!travel_technician_date_user_map.has(uid)) {
      travel_technician_date_user_map.set(uid, new TravelTechnicianDate(uid, timestamp))
    }
    return travel_technician_date_user_map.get(uid)
  }

  static async loadTravelTechnicianDatesForCalendarWeek(cw: CalendarWeek): Promise<void> {
    let cwd = PATimeControl.Instance.getCalendarWeekData(cw)
    let ttds = PATechnicianDate.travelTechnicianDates
    ttds.set(cwd.weekdays.monday.timestamp, 'loading')
    ttds.set(cwd.weekdays.tuesday.timestamp, 'loading')
    ttds.set(cwd.weekdays.wednesday.timestamp, 'loading')
    ttds.set(cwd.weekdays.thursday.timestamp, 'loading')
    ttds.set(cwd.weekdays.friday.timestamp, 'loading')
    ttds.set(cwd.weekdays.saturday.timestamp, 'loading')
    ttds.set(cwd.weekdays.sunday.timestamp, 'loading')

    const date_from = PATimeControl.Instance.dateToDatestring(new Date(cwd.weekdays['monday'].timestamp), true, false) + " 00:00:00"
    const date_until = PATimeControl.Instance.dateToDatestring(new Date(cwd.weekdays['sunday'].timestamp), true, false) + " 23:59:59"
    return new Promise((resolve, reject) => {
        this.travelTechnicianDateService.getTravelTechnicianDates(date_from, date_until).subscribe(
          async (data) => {
            ttds.set(cwd.weekdays.monday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.tuesday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.wednesday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.thursday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.friday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.saturday.timestamp, new Map<number, TravelTechnicianDate>())
            ttds.set(cwd.weekdays.sunday.timestamp, new Map<number, TravelTechnicianDate>())

            for (let ttd_hash of data) {
              let lat = ttd_hash.location_address_latitude
              let lng = ttd_hash.location_address_longitude
              let coordinates = lat && lng ? new PACoordinates(lat, lng, ttd_hash.location_name) : null
              let timestamp = PATimeControl.Instance.getUTCDayTimestampOfLocalDateString(ttd_hash.date)
              let ttd = new TravelTechnicianDate(ttd_hash.user_id, timestamp, {
                dbID: ttd_hash.id,
                coordinates: coordinates
              })
              let map = ttds.get(timestamp) as Map<number, TravelTechnicianDate>
              map.set(ttd_hash.user_id, ttd)
            }

            resolve()
          },
          (err) => {
            console.error(err)
            reject()
          },
        )
      }
    )
  }

  closestDistanceToLocation(ref_location: PALocation) {
    let closest_location_distance = -1
    for (let location of [this.getStartLocation(), ...this.tour.operations.map(op => op.ticket.location), this.getEndLocation()]) {
      location = location || this.tour.technicianDate.technician.location
      let distance = PAUtil.calcDistanceAsTheCrowFlies(location.coordinates.latitude, location.coordinates.longitude, ref_location.coordinates.latitude, ref_location.coordinates.longitude)
      if (closest_location_distance == -1 || closest_location_distance > distance) {
        closest_location_distance = distance
      }
    }
    return closest_location_distance
  }

  private insertionsMatchConstraints(insertions: Insertion[], last_visited_city_before_insertions?: string): { errors: string[] } {
    let errors: string[] = []

    errors = errors.concat(
        ...insertions.map(ins => {
          return this.insertionMatchesSLAConstraint(ins) ? [] : [`Ticket ${ins.operation.ticket.id} Warnung: SLA ${ins.operation.ticket.slaDateInformation()} `]
        }
      )
    )

    if (!this.insertionsMatchesSameCityConstraint(insertions, last_visited_city_before_insertions)) errors.push('Allg. Warnung: Keine mögliche Tour gefunden, die die Zeitbeschränkung erfüllt und jede PLZ nur ein mal besucht.')

    return { errors };
  }

  private insertionMatchesSLAConstraint(insertion: Insertion): boolean {
    if (!insertion.operation.ticket.datesla) return true
    const sla_date_timestamp = new Date(insertion.operation.ticket.datesla).getTime()
    const sla_time_diff = sla_date_timestamp - this.day.local_timestamp
    return insertion.end_time_ms <= sla_time_diff
  }

  // "No Tour should go from City A to City B and then back to A"
  private insertionsMatchesSameCityConstraint(insertions: Insertion[], last_visited_city_before_insertions?: string): boolean {
    const location_change_insertions = insertions.filter(ins => ins.operation.ticket.triggersLocationChange())
    const visited_cities: string[] = last_visited_city_before_insertions ? [last_visited_city_before_insertions] : []
    for (let insertion of location_change_insertions) {
      const city = insertion.operation.ticket.address_city
      if (city) {
        const idx = visited_cities.indexOf(city)
        if (idx >= 0 && idx != visited_cities.length - 1) {
          return false
        } else {
          if (!visited_cities.includes(city)) {
            visited_cities.push(city)
          }
        }
      }
    }
    return true
  }

  async setNewAbsences() {
    this.absences = await this.technician.getAbsencesForTimestamp(this.day.utc_timestamp)
  }

  getWorkloadOrAbsenceClass(): string {
    const day_ts = this.day.utc_timestamp
    let holidays = this.technician.getLocalPublicHolidays(day_ts)
    if (holidays.length || this.absences.length || this.noShift) {
      return 'red'
    } else {
      let workload_percent = this.tour.route.time_specific_data[PAFilterControl.Instance.selectedOperationTimeFilter].workload_percent
      if (25 > workload_percent) {
        return 'green'
      } else if (75 > workload_percent) {
        return 'yellow'
      } else {
        return 'red'
      }
    }
  }

  getTargetWorkMinutes(): number {
    const day_ts = this.day.utc_timestamp
    const weekday = this.day.week_day
    const holidays = this.technician.getLocalPublicHolidays(day_ts)

    if (holidays.length || this.absences.length || this.noShift) { return  0 }

    let absence_user = this.technician.getAbsenceUser()

    if (absence_user) {
      return absence_user.getSummedShiftMinutesOnWeekdayWithFallback(weekday, day_ts)
    } else {
      if (PATimeControl.Instance.workDays.includes(weekday)) {
        return 480
      } else {
        return 0
      }
    }
  }

  async executeOnLoaded(): Promise<void> {
    this.loadingStatus = 'loaded'
    await this.setNewAbsences()
  }
}

async function routeScore(route: PAOperation[], start_location: PALocation, end_location?: PALocation): Promise<{
  distance: number,
  duration: number
}> {
  let score = {distance: 0, duration: 0}
  let current_location = start_location
  for (let operation of route) {
    if (operation.ticket.triggersLocationChange()) {
      let operation_location = operation.ticket.client.location
      let distance = await current_location.getDistanceToLocation(operation_location)
      score.distance += distance.distance
      score.duration += distance.duration
      current_location = operation_location
    }
  }
  if (end_location) {
    let home_distance = await current_location.getDistanceToLocation(end_location)
    score.distance += home_distance.distance
    score.duration += home_distance.duration
  }
  return score
}

function sortScoredRoutes(scored_routes: ScoredRoute[]): ScoredRoute[] {

  let sortScoredRoutesByTime = (scored_permutation_a: ScoredRoute, scored_permutation_b: ScoredRoute) => {
    if (scored_permutation_a.removed_operations.length < scored_permutation_b.removed_operations.length) return -1
    if (scored_permutation_a.removed_operations.length > scored_permutation_b.removed_operations.length) return 1

    let primary_score_a = scored_permutation_a.score.duration
    let primary_score_b = scored_permutation_b.score.duration
    let secondary_score_a = scored_permutation_a.score.distance
    let secondary_score_b = scored_permutation_b.score.distance

    if (primary_score_a < primary_score_b) return -1
    if (primary_score_a > primary_score_b) return 1
    if (primary_score_a == primary_score_b) {
      if (secondary_score_a < secondary_score_b) return -1
      if (secondary_score_a >= secondary_score_b) return 1
    }
  }

  return scored_routes.sort(sortScoredRoutesByTime)
}

export interface ChangedTourContainer {
  tour: PATour
  max_tour_minutes: number;
  unfittingOperations: PAOperation[]
  deletingOperations: PAOperation[]
}

export interface OptimizedRoute {
  errors: string[],
  insertions: Insertion[],
  lunch_break?: { after_operation_idx: number, duration_minutes: number }
}

interface ScoredRoute {
  route: PAOperation[];
  score: {
    distance: number;
    duration: number;
  };
  sorted_extra_operations_with_appointment_date: PAOperation[]
  removed_operations: PAOperation[]
}

export interface DBOperationChange {
  base_operation: PAOperation
  uid_changed?: OperationUIDChanged
  date_changed?: OperationDateChanged
}

export interface OperationUIDChanged {
  current_uid: number
  db_uid: number
}

export interface OperationDateChanged {
  current_operation_date: string
  db_operation_date: string
}
